import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { House, GoogleLogo, Eye, EyeSlash, ShieldCheck, Fingerprint, KeyReturn } from '@phosphor-icons/react';

export default function LoginPage() {
  const { login, verify2FA, register, loginWithGoogle, loginWithPasskey, submitRecoveryCode, passkeySupported } = useAuth();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [twoFAEmail, setTwoFAEmail] = useState('');
  const [showRecovery, setShowRecovery] = useState(false);
  const [recEmail, setRecEmail] = useState('');
  const [recCode, setRecCode] = useState('');
  const [recNewPw, setRecNewPw] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        const res = await register(email, password, name);
        if (res.pending_approval) {
          setError('');
          setInfo(res.message);
          setIsRegister(false);
        } else {
          navigate('/dashboard');
        }
      } else {
        const res = await login(email, password);
        if (res.requires_2fa) {
          setShow2FA(true);
          setTwoFAEmail(res.email);
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    }
    setLoading(false);
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verify2FA(twoFAEmail, totpCode);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid 2FA code');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-[#0D0D0D] relative overflow-hidden" data-testid="login-page">
      {/* Ambient orbs */}
      <div className="glow-orb-orange -top-40 left-1/4 z-0" />
      <div className="glow-orb-teal bottom-0 right-0 z-0" />

      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#E85D2C] flex items-center justify-center shadow-[0_0_30px_rgba(255,107,53,0.3)]">
              <House size={24} weight="duotone" className="text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-white tracking-tight">HomeCommand</h1>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#FF6B35]">AI House Manager</p>
            </div>
          </div>

          {/* Form Card */}
          <div className="glass-card p-8">
            {showRecovery ? (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#00E5FF]/10 flex items-center justify-center">
                    <KeyReturn size={20} weight="duotone" className="text-[#00E5FF]" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-white">Recover Account</h2>
                    <p className="text-xs text-neutral-500">Use a recovery code to set a new password</p>
                  </div>
                </div>
                <form onSubmit={async (e) => {
                  e.preventDefault(); setError(''); setInfo(''); setLoading(true);
                  try {
                    const res = await submitRecoveryCode(recEmail, recCode, recNewPw);
                    setInfo(`Password reset! ${res.remaining_codes} codes remaining. You can sign in now.`);
                    setShowRecovery(false);
                    setEmail(recEmail); setPassword(recNewPw);
                    setRecEmail(''); setRecCode(''); setRecNewPw('');
                  } catch (err) {
                    setError(err.response?.data?.detail || 'Failed to use recovery code');
                  }
                  setLoading(false);
                }} className="space-y-4">
                  <div>
                    <label className="overline block mb-2">Email</label>
                    <input type="email" value={recEmail} onChange={e => setRecEmail(e.target.value)} required
                      data-testid="recovery-email-input" placeholder="your@email.com"
                      className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm placeholder-neutral-600 outline-none focus:border-[#00E5FF]/50" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Recovery Code</label>
                    <input type="text" value={recCode} onChange={e => setRecCode(e.target.value.toLowerCase())} required
                      data-testid="recovery-code-input" placeholder="apple-tiger-violet"
                      className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm placeholder-neutral-600 outline-none focus:border-[#00E5FF]/50 font-mono" />
                  </div>
                  <div>
                    <label className="overline block mb-2">New Password</label>
                    <input type="password" value={recNewPw} onChange={e => setRecNewPw(e.target.value)} required minLength={6}
                      data-testid="recovery-new-pw" placeholder="At least 6 characters"
                      className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm placeholder-neutral-600 outline-none focus:border-[#00E5FF]/50" />
                  </div>
                  {error && <p className="text-red-400 text-sm bg-red-400/10 px-4 py-2 rounded-xl">{error}</p>}
                  <button type="submit" disabled={loading} data-testid="recovery-submit-btn"
                    className="w-full py-3.5 rounded-full bg-[#00E5FF] text-black font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <KeyReturn size={16} weight="bold" />}
                    Reset Password
                  </button>
                  <button type="button" onClick={() => { setShowRecovery(false); setError(''); }}
                    className="w-full text-center text-sm text-neutral-500 hover:text-white">Back to login</button>
                </form>
              </>
            ) : show2FA ? (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#00E5FF]/10 flex items-center justify-center">
                    <ShieldCheck size={20} weight="duotone" className="text-[#00E5FF]" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-white">2FA Verification</h2>
                    <p className="text-xs text-neutral-500">Enter the code from your authenticator app</p>
                  </div>
                </div>
                <form onSubmit={handle2FASubmit} className="space-y-5">
                  <div>
                    <label className="overline block mb-2">6-Digit Code</label>
                    <input type="text" value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      data-testid="totp-input" placeholder="000000" autoFocus maxLength={6}
                      className="w-full px-4 py-4 rounded-2xl bg-white/5 border border-white/10 text-white text-2xl font-mono text-center tracking-[0.3em] placeholder-neutral-600 outline-none focus:border-[#00E5FF]/50 focus:ring-1 focus:ring-[#00E5FF]/30 transition-all duration-200" />
                  </div>
                  {error && <p className="text-red-400 text-sm bg-red-400/10 px-4 py-2 rounded-xl" data-testid="auth-error">{error}</p>}
                  <button type="submit" disabled={loading || totpCode.length < 6} data-testid="verify-2fa-btn"
                    className="w-full py-3.5 rounded-full bg-[#00E5FF] text-black font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <ShieldCheck size={16} />}
                    Verify & Sign In
                  </button>
                  <button type="button" onClick={() => { setShow2FA(false); setTotpCode(''); setError(''); }}
                    className="w-full text-center text-sm text-neutral-500 hover:text-white transition-colors">Back to login</button>
                </form>
              </>
            ) : (
              <>
            <h2 className="font-heading text-xl font-bold text-white mb-1">
              {isRegister ? 'Create Account' : 'Welcome Back'}
            </h2>
            <p className="text-sm text-neutral-500 mb-8">
              {isRegister ? 'Set up your household' : 'Sign in to your household'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {isRegister && (
                <div>
                  <label className="overline block mb-2">Full Name</label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} required
                    data-testid="name-input" placeholder="Your name"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm placeholder-neutral-600 outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30 transition-all duration-200" />
                </div>
              )}
              <div>
                <label className="overline block mb-2">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  data-testid="email-input" placeholder="admin@homecommand.local"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm placeholder-neutral-600 outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30 transition-all duration-200" />
              </div>
              <div>
                <label className="overline block mb-2">Password</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                    data-testid="password-input" placeholder="Enter password"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm placeholder-neutral-600 outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30 transition-all duration-200 pr-12" />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-300 transition-colors duration-200">
                    {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {error && <p className="text-red-400 text-sm bg-red-400/10 px-4 py-2 rounded-xl" data-testid="auth-error">{error}</p>}
              {info && <p className="text-green-400 text-sm bg-green-400/10 px-4 py-2 rounded-xl" data-testid="auth-info">{info}</p>}

              <button type="submit" disabled={loading} data-testid="login-submit-btn"
                className="w-full py-3.5 rounded-full bg-[#FF6B35] text-white font-semibold text-sm btn-glow disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : null}
                {isRegister ? 'Create Account' : 'Sign In'}
              </button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-[11px] text-neutral-500 font-medium">OR</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            {/* Google */}
            <button onClick={loginWithGoogle} data-testid="google-login-btn"
              className="w-full py-3 rounded-full bg-white/5 border border-white/10 text-white text-sm font-medium hover:bg-white/10 transition-colors duration-200 flex items-center justify-center gap-3">
              <GoogleLogo size={18} weight="bold" />
              Continue with Google
            </button>

            {/* Passkey */}
            {passkeySupported && !isRegister && (
              <button type="button" onClick={async () => {
                setError(''); setLoading(true);
                try { await loginWithPasskey(); navigate('/dashboard'); }
                catch (err) {
                  const msg = err.response?.data?.detail || err.message || 'Passkey sign-in cancelled';
                  setError(msg.includes('NotAllowed') ? 'Passkey sign-in cancelled' : msg);
                }
                setLoading(false);
              }}
                data-testid="passkey-login-btn"
                className="w-full mt-3 py-3 rounded-full bg-gradient-to-r from-[#00E5FF]/15 to-[#FF6B35]/15 border border-[#00E5FF]/30 text-white text-sm font-medium hover:from-[#00E5FF]/25 hover:to-[#FF6B35]/25 transition-all duration-200 flex items-center justify-center gap-3">
                <Fingerprint size={18} weight="duotone" className="text-[#00E5FF]" />
                Sign in with Passkey
              </button>
            )}

            {/* Forgot? Recovery code link */}
            {!isRegister && (
              <p className="text-center text-xs text-neutral-500 mt-4">
                Locked out?{' '}
                <button type="button" onClick={() => { setShowRecovery(true); setError(''); setInfo(''); }}
                  data-testid="recovery-link" className="text-[#00E5FF] font-medium hover:underline">
                  Use recovery code
                </button>
              </p>
            )}

            {/* Toggle */}
            <p className="text-center text-sm text-neutral-500 mt-6">
              {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button onClick={() => { setIsRegister(!isRegister); setError(''); }} data-testid="toggle-auth-btn"
                className="text-[#FF6B35] font-medium hover:underline">
                {isRegister ? 'Sign In' : 'Register'}
              </button>
            </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Right decorative panel */}
      <div className="hidden lg:flex flex-1 items-center justify-center relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/5 to-transparent" />
        <div className="relative z-10 text-center max-w-sm px-8">
          <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-[#FF6B35]/20 to-[#00E5FF]/10 flex items-center justify-center mx-auto mb-8 border border-white/5">
            <House size={48} weight="duotone" className="text-[#FF6B35]" />
          </div>
          <h3 className="font-heading text-2xl font-bold text-white mb-3">Your Home, Intelligent</h3>
          <p className="text-neutral-500 text-sm leading-relaxed">
            Manage staff, track expenses, plan meals, monitor health, and control your household — all from one beautiful dashboard.
          </p>
        </div>
      </div>
    </div>
  );
}
